import { api } from '@goaltree/backend/convex/_generated/api'
import { withShareUtm } from '@goaltree/backend/convex/lib/shareUtm'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import { Sheet, SheetContent, SheetTitle } from '@goaltree/ui/components/sheet'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useMutation } from 'convex/react'
import { Bell, BellOff, Check, Copy, LogOut, RefreshCw, Trash2, UserMinus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { CanopyAvatar } from '@/components/canopy-avatar'
import { UserAvatar } from '@/components/user-avatar'
import {
  optimisticDropGrove,
  optimisticRemoveGroveMember,
  optimisticSetGroveMuted,
  optimisticUpdateGrove,
} from '@/lib/groves-optimistic'

import { GroveForm } from './grove-dialogs'
import type { GroveDetail } from './grove-overview'
import { ShowcaseSettings } from './grove-showcase'
import { inviteLink, toastGroveError } from './grove-utils'

const label = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground'
const row =
  'flex w-full cursor-pointer items-center gap-3 rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted/40'

type Confirm =
  | { kind: 'leave' }
  | { kind: 'delete' }
  | { kind: 'remove'; userId: string; name: string }

export function GroveSettingsSheet({
  grove,
  open,
  onOpenChange,
}: {
  grove: GroveDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const update = useMutation(api.groves.update).withOptimisticUpdate(optimisticUpdateGrove)
  const regenerate = useMutation(api.groves.regenerateCode)
  const setMuted = useMutation(api.groves.setMuted).withOptimisticUpdate(optimisticSetGroveMuted)
  const leave = useMutation(api.groves.leave).withOptimisticUpdate(optimisticDropGrove)
  const remove = useMutation(api.groves.remove).withOptimisticUpdate(optimisticDropGrove)
  const removeMember = useMutation(api.groves.removeMember).withOptimisticUpdate(
    optimisticRemoveGroveMember,
  )
  const [copied, setCopied] = useState(false)
  const [confirm, setConfirm] = useState<Confirm | null>(null)
  const isOwner = grove.me.role === 'owner'

  async function copy() {
    try {
      await navigator.clipboard.writeText(
        withShareUtm(inviteLink(grove.inviteCode), 'grove_invite'),
      )
      setCopied(true)
      toast.success(t('groves.settings.linkCopied'))
      setTimeout(() => setCopied(false), 2000)
    } catch {}
  }

  async function runConfirm() {
    if (!confirm) return
    const c = confirm
    setConfirm(null)
    try {
      if (c.kind === 'remove') {
        await removeMember({ groveId: grove._id, userId: c.userId })
        return
      }
      onOpenChange(false)
      await (c.kind === 'leave' ? leave : remove)({ groveId: grove._id })
      navigate({ to: '/today', search: { tab: 'groves' }, replace: true })
    } catch (err) {
      toastGroveError(err, t)
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          showCloseButton={false}
          className="flex w-full flex-col gap-6 overflow-y-auto p-5 sm:max-w-sm"
        >
          <SheetTitle className="text-lg font-extrabold tracking-tight text-foreground">
            {t('groves.settings.title')}
          </SheetTitle>

          <section className="space-y-2">
            <p className={label}>{t('groves.settings.invite')}</p>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1 truncate rounded-md border border-border/60 bg-muted/20 px-3 py-2 font-mono text-xs text-muted-foreground">
                {inviteLink(grove.inviteCode)}
              </div>
              <button
                type="button"
                onClick={copy}
                className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              >
                {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                {copied ? t('common.copied') : t('groves.settings.copy')}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('groves.settings.code')}{' '}
              <span className="font-mono font-bold tracking-widest text-foreground">
                {grove.inviteCode}
              </span>
            </p>
            {isOwner && (
              <button
                type="button"
                onClick={() =>
                  regenerate({ groveId: grove._id })
                    .then(() => toast.success(t('groves.settings.codeRegenerated')))
                    .catch((err) => toastGroveError(err, t))
                }
                className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-primary-text hover:text-primary-text/80"
              >
                <RefreshCw className="size-3.5" />
                {t('groves.settings.regenerate')}
              </button>
            )}
          </section>

          <button
            type="button"
            className={row}
            onClick={() =>
              setMuted({ groveId: grove._id, muted: !grove.me.muted }).catch((err) =>
                toastGroveError(err, t),
              )
            }
          >
            {grove.me.muted ? (
              <BellOff className="size-4 text-muted-foreground" />
            ) : (
              <Bell className="size-4 text-muted-foreground" />
            )}
            <span className="flex-1">{t('groves.settings.notifications')}</span>
            <span
              className={cn(
                'relative h-5 w-9 rounded-full transition-colors',
                grove.me.muted ? 'bg-muted-foreground/30' : 'bg-primary',
              )}
            >
              <span
                className={cn(
                  'absolute top-0.5 size-4 rounded-full bg-white transition-[left]',
                  grove.me.muted ? 'left-0.5' : 'left-[18px]',
                )}
              />
            </span>
          </button>

          {isOwner && <ShowcaseSettings grove={grove} />}

          {isOwner && (
            <section className="space-y-2">
              <p className={label}>{t('groves.settings.details')}</p>
              <GroveForm
                key={`${grove.name}|${grove.icon}|${grove.description ?? ''}`}
                initial={{
                  name: grove.name,
                  icon: grove.icon,
                  description: grove.description ?? '',
                }}
                submitLabel={t('common.save')}
                onSubmit={async (v) => {
                  try {
                    await update({ groveId: grove._id, ...v })
                    toast.success(t('groves.settings.saved'))
                  } catch (err) {
                    toastGroveError(err, t)
                  }
                }}
              />
            </section>
          )}

          {isOwner && grove.members.length > 1 && (
            <section className="space-y-1">
              <p className={label}>{t('groves.membersTitle')}</p>
              <div className="divide-y divide-border/40">
                {grove.members
                  .filter((m) => !m.isMe)
                  .map((m) => (
                    <div key={m.userId} className="flex items-center gap-3 py-2">
                      <CanopyAvatar userId={m.userId}>
                        <UserAvatar
                          name={m.name}
                          image={m.image}
                          className="size-8 shrink-0 rounded-full object-cover"
                        />
                      </CanopyAvatar>
                      <p className="min-w-0 flex-1 truncate text-sm font-semibold">{m.name}</p>
                      <button
                        type="button"
                        aria-label={t('groves.settings.remove', { name: m.name })}
                        onClick={() =>
                          setConfirm({ kind: 'remove', userId: m.userId, name: m.name })
                        }
                        className="flex size-8 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                      >
                        <UserMinus className="size-4" />
                      </button>
                    </div>
                  ))}
              </div>
            </section>
          )}

          <section className="mt-auto space-y-2">
            <button type="button" className={row} onClick={() => setConfirm({ kind: 'leave' })}>
              <LogOut className="size-4 text-muted-foreground" />
              {t('groves.settings.leave')}
            </button>
            {isOwner && (
              <button
                type="button"
                className={cn(row, 'text-destructive')}
                onClick={() => setConfirm({ kind: 'delete' })}
              >
                <Trash2 className="size-4" />
                {t('groves.settings.delete')}
              </button>
            )}
          </section>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm &&
                t(`groves.confirm.${confirm.kind}Title`, {
                  name: confirm.kind === 'remove' ? confirm.name : grove.name,
                })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm &&
                t(`groves.confirm.${confirm.kind}Desc`, {
                  context: confirm.kind === 'leave' && isOwner ? 'owner' : undefined,
                })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirm}>
              {confirm && t(`groves.confirm.${confirm.kind}`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
